import React, { useState, useEffect, useRef, useMemo } from 'react';
import { ChevronDown, Search } from 'lucide-react';
import { buscarPaises, paisPorIso } from './paises';

// Imagen y no emoji: en Windows los emojis de bandera se ven como dos letras.
const Bandera = ({ iso }) => (
  <img
    src={`https://flagcdn.com/w20/${iso.toLowerCase()}.png`}
    srcSet={`https://flagcdn.com/w40/${iso.toLowerCase()}.png 2x`}
    width={20}
    height={15}
    alt=""
    loading="lazy"
    className="rounded-sm shadow-[0_0_0_1px_rgba(0,0,0,0.08)] flex-shrink-0"
  />
);

// value: { iso, numero }. El número se guarda tal como se escribe; el
// formulario arma el número internacional al enviar.
const CampoTelefono = ({ value, onChange, required }) => {
  const [abierto, setAbierto] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const [resaltado, setResaltado] = useState(0);
  const contenedorRef = useRef(null);
  const busquedaRef = useRef(null);
  const listaRef = useRef(null);
  const numeroRef = useRef(null);

  const pais = paisPorIso(value.iso);
  const resultados = useMemo(() => buscarPaises(busqueda), [busqueda]);

  useEffect(() => {
    if (!abierto) return undefined;
    busquedaRef.current?.focus();
    const alClicFuera = (e) => {
      if (!contenedorRef.current?.contains(e.target)) setAbierto(false);
    };
    document.addEventListener('mousedown', alClicFuera);
    return () => document.removeEventListener('mousedown', alClicFuera);
  }, [abierto]);

  useEffect(() => { setResaltado(0); }, [busqueda]);

  useEffect(() => {
    listaRef.current?.children[resaltado]?.scrollIntoView({ block: 'nearest' });
  }, [resaltado]);

  const elegir = (p) => {
    onChange({ ...value, iso: p.iso });
    setAbierto(false);
    setBusqueda('');
    numeroRef.current?.focus();
  };

  const teclaEnBusqueda = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setResaltado(i => Math.min(i + 1, resultados.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setResaltado(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (resultados[resaltado]) elegir(resultados[resaltado]);
    } else if (e.key === 'Escape') {
      e.stopPropagation();
      setAbierto(false);
    }
  };

  return (
    <div ref={contenedorRef} className="relative">
      <div className="flex rounded-lg border border-gray-300 focus-within:ring-2 focus-within:ring-blue-500">
        <button
          type="button"
          onClick={() => setAbierto(a => !a)}
          aria-haspopup="listbox"
          aria-expanded={abierto}
          aria-label={`Código de país: ${pais.nombre} +${pais.codigo}`}
          className="flex items-center gap-1.5 pl-3 pr-2 py-2 bg-gray-50 border-r border-gray-300 rounded-l-lg text-sm text-gray-700 hover:bg-gray-100 outline-none"
        >
          <Bandera iso={pais.iso} />
          <span className="tabular-nums">+{pais.codigo}</span>
          <ChevronDown size={14} className={`text-gray-400 transition-transform ${abierto ? 'rotate-180' : ''}`} />
        </button>
        <input
          ref={numeroRef}
          type="tel"
          inputMode="tel"
          value={value.numero}
          onChange={(e) => onChange({ ...value, numero: e.target.value.replace(/[^\d\s-]/g, '') })}
          required={required}
          placeholder={pais.iso === 'VE' ? '414 123 4567' : 'Número'}
          className="flex-1 min-w-0 px-3 py-2 text-sm rounded-r-lg outline-none tabular-nums"
        />
      </div>

      {abierto && (
        <div className="absolute z-20 mt-1 w-72 max-w-[calc(100vw-2rem)] bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden">
          <div className="p-2 border-b border-gray-100">
            <div className="relative">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                ref={busquedaRef}
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                onKeyDown={teclaEnBusqueda}
                placeholder="Buscar país o código"
                className="w-full pl-8 pr-2 py-1.5 text-sm border border-gray-200 rounded-lg outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          <ul ref={listaRef} role="listbox" className="max-h-64 overflow-y-auto py-1">
            {resultados.length === 0 ? (
              <li className="px-3 py-4 text-sm text-gray-400 text-center">Ningún país coincide con “{busqueda}”</li>
            ) : resultados.map((p, i) => (
              <li
                key={p.iso}
                role="option"
                aria-selected={p.iso === value.iso}
                onMouseDown={(e) => { e.preventDefault(); elegir(p); }}
                onMouseEnter={() => setResaltado(i)}
                className={`flex items-center gap-2.5 px-3 py-1.5 text-sm cursor-pointer ${
                  i === resaltado ? 'bg-blue-50' : ''
                } ${p.iso === value.iso ? 'font-medium text-blue-700' : 'text-gray-700'} ${
                  i === 0 && !busqueda ? 'border-b border-gray-100 mb-1 pb-2' : ''
                }`}
              >
                <Bandera iso={p.iso} />
                <span className="flex-1 truncate">{p.nombre}</span>
                <span className="text-gray-400 tabular-nums">+{p.codigo}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default CampoTelefono;
